import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-medium-emphasis text-caption" }

import { ref, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/main';
import language from "@/API/Language/Language";

interface Language {
  id: string;
  code: string;
  name: string;
  order: number;
  rtl: string;
  flag: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'languageView',
  setup(__props) {

const { t } = useI18n();

const active = ref<boolean>(false);
const languages = ref<Language[]>([]);
const selectedLanguage = ref<Language | null>(null);
const languageApi = new language();

// Reactive RTL state
const isRTL = ref<boolean>(
  document.documentElement.dir === 'rtl' ||
  localStorage.getItem('selectedLanguageCode') === 'ar' ||
  document.documentElement.getAttribute('lang') === 'ar'
);

async function fetchLanguages() {
  try {
    const response = await languageApi.getAll() as Language[];
    languages.value = response.sort((a: Language, b: Language) => 
      Number(a.order) - Number(b.order)
    );
    
    // Load saved language from localStorage if available
    const savedLangCode = localStorage.getItem('selectedLanguageCode');
    const savedLang = languages.value.find(lang => lang.code.toLowerCase() === savedLangCode?.toLowerCase());
    if (savedLang) {
      i18n.global.locale.value = savedLang.code.toLowerCase();
      selectLanguage(savedLang, false);
    } else if (!selectedLanguage.value && languages.value.length > 0) {
      selectLanguage(languages.value[0], false);
    }
  } catch (error) {
    console.error('Error fetching languages:', error);
  }
}

function selectLanguage(language: Language, refresh = true) {
  active.value = false;
  (i18n.global.locale as any).value = language.code.toLowerCase();
  selectedLanguage.value = language;
  
  // Save selected language code to localStorage
  localStorage.setItem('selectedLanguageCode', language.code.toLowerCase());
  
  // Update RTL state
  const isRtlLanguage = language.rtl === 'true';
  isRTL.value = isRtlLanguage;
  
  // Set direction based on RTL flag
  document.documentElement.dir = isRtlLanguage ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('lang', language.code.toLowerCase());
  
  // Update body class for additional styling if needed
  if (isRtlLanguage) {
    document.body.classList.add('rtl');
    document.body.classList.remove('ltr');
  } else {
    document.body.classList.add('ltr');
    document.body.classList.remove('rtl');
  }
  
  if (refresh) {
    window.location.reload();
  }
}

onMounted(() => {
  fetchLanguages();
  updateRTLState();
  
  // Set up mutation observer to watch for direction changes
  const observer = new MutationObserver(() => {
    updateRTLState();
  });
  
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['dir', 'lang']
  });
});

// Force position the dropdown based on RTL/LTR when it opens
watch(active, (newVal) => {
  if (newVal) {
    nextTick(() => {
      const dropdownElement = document.querySelector('.language-dropdown-menu .v-overlay__content') as HTMLElement;
      if (dropdownElement) {
        dropdownElement.style.position = 'fixed';
        dropdownElement.style.transform = 'none';
        dropdownElement.style.zIndex = '9999';
        
        if (isRTL.value) {
          dropdownElement.style.left = '20px';
          dropdownElement.style.right = 'auto';
        } else {
          dropdownElement.style.right = '20px';
          dropdownElement.style.left = 'auto';
        }
      }
    });
  }
});

// Update RTL state when document direction changes
const updateRTLState = () => {
  const newRTL = document.documentElement.dir === 'rtl' ||
                 localStorage.getItem('selectedLanguageCode') === 'ar';
  if (newRTL !== isRTL.value) {
    isRTL.value = newRTL;
  }
};

// Watch for direction changes
watch(isRTL, (newVal) => {
  // Force update dropdown position if it's open
  if (active.value) {
    nextTick(() => {
      const dropdownElement = document.querySelector('.language-dropdown-menu .v-overlay__content') as HTMLElement;
      if (dropdownElement) {
        if (newVal) {
          dropdownElement.style.left = '20px';
          dropdownElement.style.right = 'auto';
        } else {
          dropdownElement.style.right = '20px';
          dropdownElement.style.left = 'auto';
        }
      }
    });
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_menu = _resolveComponent("v-menu")!

  return (_openBlock(), _createBlock(_component_v_menu, {
    modelValue: active.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((active).value = $event)),
    "close-on-content-click": false,
    location: isRTL.value ? 'bottom end' : 'bottom start',
    offset: "4",
    attach: false,
    "content-class": "language-dropdown-menu",
    class: _normalizeClass({ 'rtl-menu': isRTL.value })
  }, {
    activator: _withCtx(({ props }) => [
      _createVNode(_component_v_btn, _mergeProps(props, {
        variant: "outlined",
        class: "text-none language-selector-btn",
        style: {"background-color":"#F5F7FA"},
        "prepend-icon": `flag-icon flag-icon-${selectedLanguage.value?.flag}`
      }), {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(selectedLanguage.value?.code) + " ", 1),
          _createVNode(_component_v_icon, {
            class: _normalizeClass(isRTL.value ? 'mr-1' : 'ml-1')
          }, {
            default: _withCtx(() => _cache[1] || (_cache[1] = [
              _createTextVNode("mdi-chevron-down", -1)
            ])),
            _: 1,
            __: [1]
          }, 8, ["class"])
        ]),
        _: 2
      }, 1040, ["prepend-icon"])
    ]),
    default: _withCtx(() => [
      _createVNode(_component_v_card, { "min-width": "250" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_list, { density: "compact" }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(languages.value, (lang) => {
                return (_openBlock(), _createBlock(_component_v_list_item, {
                  key: lang.id,
                  onClick: ($event: any) => (selectLanguage(lang)),
                  class: "cursor-pointer"
                }, {
                  prepend: _withCtx(() => [
                    _createElementVNode("i", {
                      class: _normalizeClass(`flag-icon flag-icon-${lang.flag} m-rtl`)
                    }, null, 2)
                  ]),
                  append: _withCtx(() => [
                    _createElementVNode("span", _hoisted_1, _toDisplayString(lang.code), 1)
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(lang.name), 1)
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _: 2
                }, 1032, ["onClick"]))
              }), 128))
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue", "location", "class"]))
}
}

})